import { attachClientTelemetry } from 'Shared/network/telemetryInterceptor';
import { handleDefaultError } from 'Shared/utils';
import axios from 'axios';

/**
 * nice 본인인증 encode 전용 transport. 계측만 붙은 인터셉터 없는 인스턴스다.
 *
 * **왜 정본 `Shared/network`의 `httpClient`를 안 쓰는가** — 그 인스턴스는 `ErrorInterceptor`가
 * 네트워크 에러를 `NetworkError`로, `code === 'unknown'`을 `UnknownError`로 갈아치우고
 * `AuthInterceptor`가 401을 `AuthorizationError`로 바꾼다. 그런데 이 흐름의 유일한 에러 처리기인
 * `handleDefaultError`는 `AxiosError`가 아니면 **조용히 return**한다 — 그대로 얹으면 사용자가
 * 본인인증 버튼을 눌렀는데 아무 반응도 없는 상태가 된다(가입 / 아이디찾기 / 비밀번호재설정 /
 * CI 추가 / 재인증 5개 흐름). `auth-009`처럼 의도적으로 조용히 넘기던 경로도 refresh 흐름을 타며
 * 뒤틀린다.
 *
 * 그래서 raw `AxiosError`가 그대로 `handleDefaultError`까지 도달하도록 인터셉터를 두지 않고,
 * SigNoz 계측만 붙인다. breadcrumb·traceparent는 얻고 에러 의미는 그대로다.
 * (bare `axios.get`이던 시절엔 이 요청이 breadcrumb에 안 남아, 직후 에러 리포트가 낡은 다른
 *  트레이스에 묶이는 구멍이었다 — `shared/CLAUDE.md`의 "모든 transport를 계측한다" 불변식.)
 *
 * ⚠️ `reAuthentication`·`resetPassword` 모드에서는 이 URL이 `redirectUrl` 안에 계정 아이디(`?id=`)를
 * 품는다(나머지 3개 모드는 id를 붙이지 않는다). SDK가 `config.url`을 **무가공으로**
 * breadcrumb에 기록하므로 그 아이디가 에러 리포트 payload에 실린다(본인의 리포트에 본인 식별자가
 * 담기는 형태). 앱의 다른 요청도 `?snsAccountUuid=` 등을 같은 방식으로 싣고 있어 이 경로만의
 * 문제는 아니다 — 근본 해결은 SDK가 breadcrumb URL에서 쿼리스트링을 제거하는 것이다(별건).
 */
const niceAuthClient = axios.create();
attachClientTelemetry(niceAuthClient);

export const niceAuth = ({
  redirectPage,
  decodeMode,
  id,
}: {
  decodeMode: 'register' | 'findId' | 'resetPassword' | 'addCi' | 'reAuthentication';
  id?: string;
  redirectPage: string;
}) => {
  // 아이디는 redirectUrl 쿼리로 서버까지 전달돼 계정 아이디와 완전 일치 비교된다(불일치 시 nice-024).
  // 자동완성이 붙인 앞뒤 공백이 그대로 전달되면 정상 아이디도 불일치 판정을 받으므로 여기서 정규화한다.
  const normalizedId = encodeURIComponent(id?.trim() ?? '');
  const redirectUrl =
    decodeMode === 'reAuthentication' || decodeMode === 'resetPassword'
      ? `${window.location.origin}${redirectPage}?id=${normalizedId}`
      : `${window.location.origin}${redirectPage}`;
  const returnUrl = `${process.env.NEXT_PUBLIC_VIEWUP_API_URL}/v2/nice/decode-nice-api/${decodeMode}?redirectUrl=${encodeURIComponent(redirectUrl)}`;

  // URL은 절대경로 문자열 그대로 유지한다. axios `params`로 옮기면 returnUrl 안에 raw로 들어있는
  // `?redirectUrl=`이 한 겹 더 percent-encode되어 서버가 받는 쿼리스트링이 달라진다.
  niceAuthClient
    .get(`${process.env.NEXT_PUBLIC_VIEWUP_API_URL}/v2/nice/encode-nice-api?returnUrl=${returnUrl}`)
    .then(
      ({
        data: {
          data: { enc_data, integrity, token_version_id },
        },
      }) => {
        const niceAuth = 'popupNiceAuth';
        const result = window.open('', niceAuth, 'width=300, height=400');
        if (result == null || result === undefined) {
          return alert('팝업차단을 허용해주시기 바랍니다.');
        }

        const form = document.createElement('form');
        form.action = 'https://nice.checkplus.co.kr/CheckPlusSafeModel/checkplus.cb';
        form.target = niceAuth;
        form.method = 'post';
        form.name = 'form_chk';

        const encodeDataInput = document.createElement('input');
        encodeDataInput.type = 'hidden';
        encodeDataInput.name = 'enc_data';
        encodeDataInput.value = enc_data;

        const integrityValueInput = document.createElement('input');
        integrityValueInput.type = 'hidden';
        integrityValueInput.name = 'integrity_value';
        integrityValueInput.value = integrity;

        const tokenVersionIdInput = document.createElement('input');
        tokenVersionIdInput.type = 'hidden';
        tokenVersionIdInput.name = 'token_version_id';
        tokenVersionIdInput.value = token_version_id;

        const mInput = document.createElement('input');
        mInput.type = 'hidden';
        mInput.name = 'm';
        mInput.value = 'checkplusService';

        form.appendChild(encodeDataInput);
        form.appendChild(integrityValueInput);
        form.appendChild(tokenVersionIdInput);
        form.appendChild(mInput);
        document.body.appendChild(form);

        form.submit();
      },
    )
    .catch((err) => handleDefaultError(err));
};
